import { motion } from "framer-motion";
import { InstagramLogo, YoutubeLogo, ArrowUpRight } from "@phosphor-icons/react";
import { properties } from "@/data/properties";
import { INSTAGRAM_URL, YOUTUBE_URL, INSTAGRAM_EMBED_ENABLED } from "@/data/social";
import InstagramEmbed from "@/components/site/InstagramEmbed";
import Seo, { SITE } from "@/components/site/Seo";
import { breadcrumbJsonLd } from "@/lib/schema";

const IG = INSTAGRAM_URL;
const YT = YOUTUBE_URL;

// Social grid built entirely from real Double Rainbow Suites property photos.
const gallery = [
  properties[0].image,
  properties[1].image,
  properties[2].image,
  properties[3].image,
  properties[0].gallery?.[3] || properties[0].image,
  properties[3].gallery?.[2] || properties[3].image,
];

export default function Social() {
  return (
    <main className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title="Social | Double Rainbow Suites on Instagram & YouTube"
        description="Follow Double Rainbow Suites @doublerainbowsuites on Instagram and YouTube for property tours, new reveals and South Florida travel inspiration."
        canonical={`${SITE}/social`}
        jsonLd={breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Social", path: "/social" },
        ])}
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="overline text-sunset mb-5">Follow along</p>
          <h1 className="font-display text-5xl md:text-8xl leading-[0.88] tracking-tight max-w-4xl">
            Life at the Suites, in motion.
          </h1>
          <p className="text-lg text-emerald-soft max-w-xl mt-6 font-light">
            Behind-the-scenes tours, new property reveals and a little South Florida sunshine — all @doublerainbowsuites.
          </p>
        </motion.div>

        {/* Channel cards */}
        <div className="grid md:grid-cols-2 gap-5 md:gap-6 mt-14">
          <a href={IG} target="_blank" rel="noopener noreferrer" data-testid="social-instagram-card" className="group relative overflow-hidden rounded-2xl p-8 md:p-10 bg-gradient-to-br from-sunset to-gold text-sand min-h-[220px] flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <InstagramLogo size={44} weight="duotone" />
              <ArrowUpRight size={26} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
            <div>
              <p className="font-display text-4xl md:text-5xl">Instagram</p>
              <p className="text-sand/90 mt-1">@doublerainbowsuites</p>
            </div>
          </a>
          <a href={YT} target="_blank" rel="noopener noreferrer" data-testid="social-youtube-card" className="group relative overflow-hidden rounded-2xl p-8 md:p-10 bg-emerald text-sand min-h-[220px] flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <YoutubeLogo size={44} weight="duotone" className="text-sunset" />
              <ArrowUpRight size={26} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
            <div>
              <p className="font-display text-4xl md:text-5xl">YouTube</p>
              <p className="text-sand/80 mt-1">@doublerainbowsuites</p>
            </div>
          </a>
        </div>

        {/* Live Instagram embed */}
        {INSTAGRAM_EMBED_ENABLED && (
          <div className="mt-16">
            <div className="flex items-center justify-between mb-6">
              <p className="overline text-sunset">Live on Instagram</p>
              <span className="flex items-center gap-2 text-xs text-emerald-soft"><span className="w-2 h-2 rounded-full bg-sage animate-pulse" /> Live</span>
            </div>
            <InstagramEmbed />
          </div>
        )}

        {/* Curated highlights */}
        <div className="mt-16">
          <p className="overline text-sunset mb-6">{INSTAGRAM_EMBED_ENABLED ? "A peek inside" : "From the feed"}</p>
          <div className="columns-2 md:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
            {gallery.map((src, i) => (
              <motion.a
                key={i}
                href={IG}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className={`group block break-inside-avoid mb-4 md:mb-6 relative overflow-hidden rounded-xl ${i % 2 === 0 ? "aspect-[4/5]" : "aspect-square"}`}
                data-testid={`feed-item-${i}`}
              >
                <img src={src} alt="Double Rainbow Suites" className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-110" />
                <div className="absolute inset-0 bg-emerald/0 group-hover:bg-emerald/30 transition-colors duration-300 grid place-items-center">
                  <InstagramLogo size={28} className="text-sand opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
